import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/* Models */
import { AppData } from '@core/models/app-data.model';
import { Data } from '@core/models/data.model';
import { LatestData } from '@core/models/latest-data.model';
import { NodeDetail } from '@core/models/node-detail.model';
import { AuthObj, UserAccountResponse } from '@core/models/user.model';
import { NodeData, NodeDetails } from '@modules/dashboard/models'; // TODO: move these!
import { CustomerListItem } from '@core/models/customer.model';
import { SmartCleaning } from '@core/store/dashboard/dashboard.model';
import { PortfolioItem } from '@core/store/portfolio/portfolio.model';

export interface LoginResponse {
	isAdmin?: boolean;
	token?: string;
	tfa?: string;
	data?: AppData;
	message?: string;
	maxAge: number;
}

export interface DashboardDataResponse {
	nodeData?: NodeData[];
	nodeDetails?: NodeDetails;
	detailedData?: any[] | null;
	type?: string;
}

export interface SetAuthResponse {
	dataURL: string;
	tempSecret: string;
	tfaURL: string;
}

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	constructor(private http: HttpClient) {}

	getCustomerList(): Observable<CustomerListItem[]> {
		return this.http.get<CustomerListItem[]>('/api/customers');
	}

	getNodeData(params: any): Observable<Data[]> {
		Object.keys(params).forEach((key) => params[key] == null && delete params[key]);

		return this.http.get<Data[]>('/api/data', { params }).pipe(catchError(this.handleError));
	}

	getDashboardData(params: any): Observable<DashboardDataResponse> {
		Object.keys(params).forEach((key) => params[key] == null && delete params[key]);

		return this.http
			.get<DashboardDataResponse>('/api/dashboard-data', { params })
			.pipe(catchError(this.handleError));
	}

	getPortfolioOverview(params: any): Observable<PortfolioItem[]> {
		Object.keys(params).forEach((key) => params[key] == null && delete params[key]);

		return this.http.get<any>('/api/portfolio-overview', { params }).pipe(catchError(this.handleError));
	}

	getNodeDetails(nodeId: string): Observable<NodeDetail> {
		return this.http.get<NodeDetail>('api/node-detail/' + nodeId).pipe(catchError(this.handleError));
	}

	getNodesDetails(nodeIds: number[]): Observable<NodeDetail[]>
	{
		return this.http.get<NodeDetail[]>('api/node-detail/0?ids=' + nodeIds.join(',')).pipe(catchError(this.handleError));
	}

	getDetailedData(nodeId: string, params: any): Observable<Data[]> {
		Object.keys(params).forEach((key) => params[key] == null && delete params[key]);

		return this.http.get<Data[]>('/api/data/' + nodeId, { params }).pipe(catchError(this.handleError));
	}

	getSmartCleaning(params: any): Observable<SmartCleaning> {
		return this.http.get<SmartCleaning>('/api/smart-cleaning', { params }).pipe(catchError(this.handleError));
	}

	getLatestData(params: any): Observable<LatestData[]> {
		return this.http.get<LatestData[]>('/api/latest-data', { params }).pipe(catchError(this.handleError));
	}

	updateNodeDetails(nodeId: string | number, params: any): Observable<any> {
		return this.http.put<any>('api/node-detail/' + nodeId, params).pipe(catchError(this.handleError));
	}

	refreshSession(): Promise<number>
	{
		return this.http.get<number>('/api/auth', {}).pipe(catchError(this.handleError)).toPromise();
	}

	authenticateUser(authObj: AuthObj): Observable<HttpResponse<LoginResponse>> {
		return this.http
			.post<LoginResponse>('/api/auth', authObj, { observe: 'response' })
			.pipe(catchError(this.handleError));
	}

	setTwoFactorAuth(action: string, options: object): Observable<SetAuthResponse | boolean | string[]> {
		return this.http
			.post<SetAuthResponse | boolean | string[]>('/api/tfa/' + action, options)
			.pipe(catchError(this.handleError));
	}

	logoutUser(): Observable<object> {
		return this.http.delete('/api/auth').pipe(catchError(this.handleError));
	}

	updateUserAccount(body: any): Observable<UserAccountResponse> {
		return this.http.put<UserAccountResponse>('/api/auth', body).pipe(catchError(this.handleError));
	}

	resetPassword(params: any): Observable<boolean> {
		if (params.email) {
			return this.http.post<boolean>('/api/reset-password', { params }).pipe(catchError(this.handleError));
		}

		return this.http.put<boolean>('/api/reset-password', { params }).pipe(catchError(this.handleError));
	}

	// createAccount(params: any): Observable<RegisterResponse> {
	// 	return this.http.post<RegisterResponse>('/api/Register', { params }).pipe(
	// 		catchError(this.handleError)
	// 	);
	// }

	// checkIfExists(params: any) {
	// 	return this.http.post<any>('/api/Register', { params }).pipe(
	// 		catchError(this.handleError)
	// 	);
	// }

	private handleError(err: any): Observable<never> {
		let errorMessage: string;
		if (err.error instanceof ErrorEvent) {
			// A client-side or network error occurred.
			errorMessage = `An error occurred: ${err.error}`;
		} else {
			// The backend returned an unsuccessful response code.
			console.log('API Service Error: ', err);
			errorMessage = err.error.message; //.replace('ERROR', '').trim();
		}

		return throwError(errorMessage);
	}
}
